import React, { useEffect, useState } from 'react';
import cn from "classnames";
import styles from "./TutorInfo.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Switch from "../../../components/Switch";
import DualButton from "../../../components/DualButton";
import PhoneInput from "../../../components/PhoneInput";
import TutorSearchBar from "../../../components/TutorSearchBar";
import { createTutor } from "../../../services/tutor.service";
import { updateFolder } from "../../../services/folder.service";
import { toast } from "react-toastify";

const DEGREE_LEVEL_OPTIONS = [
  { value: '3', title: '3 : CAP, BEP' },
  { value: '4', title: '4 : Baccalauréat' },
  { value: '5', title: '5 : DEUG, BTS, DUT, DEUST' },
  { value: '6', title: '6 : Licence, licence professionnelle, BUT, Maîtrise' },
  { value: '7', title: '7 : Master, diplôme d\'études approfondies, diplôme d\'études supérieures spécialisées, diplôme d\'ingénieur' },
  { value: '8', title: '8 : Doctorat, habilitation à diriger des recherches' },
  { value: '0', title: '0 : Aucun' },
];

const TutorInfo = ({ className, data, updateData, currentStep, folderId }) => {
  // Récupérer les données du contrat et des tuteurs depuis les props
  const contractInfo = data?.contract?.generalInfo || {};

  // État local pour le second tuteur
  const [hasSecondTutor, setHasSecondTutor] = useState(contractInfo.has_second_tutor || 'Non');

  // États pour les modes de saisie
  const [tutorMode, setTutorMode] = useState('create'); // 'search' ou 'create'
  const [secondTutorMode, setSecondTutorMode] = useState('create'); // 'search' ou 'create'
  
  // États pour les tuteurs sélectionnés (pour éviter les doublons)
  const [selectedTutorId, setSelectedTutorId] = useState(null);
  const [selectedSecondTutorId, setSelectedSecondTutorId] = useState(null);

  // États pour le tuteur principal
  const [tutorData, setTutorData] = useState({
    last_name: contractInfo.tutor?.last_name || '',
    first_name: contractInfo.tutor?.first_name || '',
    birth_date: contractInfo.tutor?.birth_date || '',
    email: contractInfo.tutor?.email || '',
    phone: contractInfo.tutor?.phone || '',
    job_title: contractInfo.tutor?.job_title || '',
    highest_degree_title: contractInfo.tutor?.highest_degree_title || '',
    highest_degree_level: contractInfo.tutor?.highest_degree_level || '',
    verified: contractInfo.tutor?.verified || false,
    is_apt_tutor: contractInfo.tutor?.is_apt_tutor || false,
  });

  // États pour le second tuteur
  const [secondTutorData, setSecondTutorData] = useState({
    last_name: contractInfo.second_tutor?.last_name || '',
    first_name: contractInfo.second_tutor?.first_name || '',
    birth_date: contractInfo.second_tutor?.birth_date || '',
    email: contractInfo.second_tutor?.email || '',
    phone: contractInfo.second_tutor?.phone || '',
    job_title: contractInfo.second_tutor?.job_title || '',
    highest_degree_title: contractInfo.second_tutor?.highest_degree_title || '',
    highest_degree_level: contractInfo.second_tutor?.highest_degree_level || '',
    verified: contractInfo.second_tutor?.verified || false,
    is_apt_tutor: contractInfo.second_tutor?.is_apt_tutor || false,
  });

  // Effet pour initialiser les données du contrat et des tuteurs lorsqu'elles changent
  useEffect(() => {
    if (data?.contract?.generalInfo) {
      const contractInfo = data.contract.generalInfo;

      // Mettre à jour l'état local pour le second tuteur
      setHasSecondTutor(contractInfo.has_second_tutor || 'Non');

      // Mettre à jour les données du tuteur principal
      if (contractInfo.tutor) {
        setTutorData({
          last_name: contractInfo.tutor.last_name || '',
          first_name: contractInfo.tutor.first_name || '',
          birth_date: contractInfo.tutor.birth_date || '',
          email: contractInfo.tutor.email || '',
          phone: contractInfo.tutor.phone || '',
          job_title: contractInfo.tutor.job_title || '',
          highest_degree_title: contractInfo.tutor.highest_degree_title || '',
          highest_degree_level: contractInfo.tutor.highest_degree_level || '',
          verified: contractInfo.tutor.verified || false,
          is_apt_tutor: contractInfo.tutor.is_apt_tutor || false,
        });
      }

      // Mettre à jour les données du second tuteur
      if (contractInfo.second_tutor) {
        setSecondTutorData({
          last_name: contractInfo.second_tutor.last_name || '',
          first_name: contractInfo.second_tutor.first_name || '',
          birth_date: contractInfo.second_tutor.birth_date || '',
          email: contractInfo.second_tutor.email || '',
          phone: contractInfo.second_tutor.phone || '',
          job_title: contractInfo.second_tutor.job_title || '',
          highest_degree_title: contractInfo.second_tutor.highest_degree_title || '',
          highest_degree_level: contractInfo.second_tutor.highest_degree_level || '',
          verified: contractInfo.second_tutor.verified || false,
          is_apt_tutor: contractInfo.second_tutor.is_apt_tutor || false,
        });
      }
    }
  }, [data?.contract?.generalInfo]);

  // Gestionnaires pour les changements de données
  const handleInputChange = (name, value) => {
    if (updateData) {
      updateData('generalInfo', { ...data.contract?.generalInfo, [name]: value });
    }
  };

  const handleTutorChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    // Mettre à jour l'état local du tuteur
    const updatedTutorData = {
      ...tutorData,
      [name]: newValue
    };

    setTutorData(updatedTutorData);

    // Mettre à jour les données du tuteur dans le contrat
    // et s'assurer que tutor_id est défini si nous avons des données de tuteur
    if (updatedTutorData.last_name || updatedTutorData.first_name) {
      handleInputChange('tutor', updatedTutorData);
      // Assurer que tutor_id est défini pour la liaison
      if (contractInfo.tutor_id) {
        handleInputChange('tutor_id', contractInfo.tutor_id);
      }
    }
  };

  const handleTutorSwitchChange = (name, value) => {
    const updatedTutorData = { ...tutorData, [name]: value };
    setTutorData(updatedTutorData);

    // Mettre à jour les données du tuteur dans le contrat
    handleInputChange('tutor', updatedTutorData);

    // Assurer que tutor_id est défini pour la liaison
    if (contractInfo.tutor_id) {
      handleInputChange('tutor_id', contractInfo.tutor_id);
    }
  };

  const handleSecondTutorChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    // Mettre à jour l'état local du second tuteur
    const updatedSecondTutorData = {
      ...secondTutorData,
      [name]: newValue
    };

    setSecondTutorData(updatedSecondTutorData);

    // Mettre à jour les données du second tuteur dans le contrat
    // et s'assurer que second_tutor_id est défini si nous avons des données de tuteur
    if (updatedSecondTutorData.last_name || updatedSecondTutorData.first_name) {
      handleInputChange('second_tutor', updatedSecondTutorData);
      // Assurer que second_tutor_id est défini pour la liaison
      if (contractInfo.second_tutor_id) {
        handleInputChange('second_tutor_id', contractInfo.second_tutor_id);
      }
    }
  };

  const handleSecondTutorSwitchChange = (name, value) => {
    const updatedSecondTutorData = { ...secondTutorData, [name]: value };
    setSecondTutorData(updatedSecondTutorData);

    // Mettre à jour les données du second tuteur dans le contrat
    handleInputChange('second_tutor', updatedSecondTutorData);

    // Assurer que second_tutor_id est défini pour la liaison
    if (contractInfo.second_tutor_id) {
      handleInputChange('second_tutor_id', contractInfo.second_tutor_id);
    }
  };

  const handleDualButtonChange = (name, value) => {
    setHasSecondTutor(value);
    handleInputChange('has_second_tutor', value);
  };

  // Gérer la sélection d'un tuteur existant
  const handleSelectExistingTutor = async (tutor) => {
    if (!tutor) return;
    
    // Vérifier si ce tuteur est déjà sélectionné comme second tuteur
    if (selectedSecondTutorId === tutor.id) {
      toast.error('Ce tuteur est déjà sélectionné comme second maître d\'apprentissage');
      return;
    }
    
    // Mettre à jour les données du tuteur pour l'affichage
    const tutorDataObj = {
      last_name: tutor.last_name || '',
      first_name: tutor.first_name || '',
      birth_date: tutor.birth_date || '',
      email: tutor.email || '',
      phone: tutor.phone || '',
      job_title: tutor.job_title || '',
      highest_degree_title: tutor.highest_degree_title || '',
      highest_degree_level: tutor.highest_degree_level || '',
      verified: tutor.verified || false,
      is_apt_tutor: tutor.is_apt_tutor || false,
    };
    
    setTutorData(tutorDataObj);
    setSelectedTutorId(tutor.id);
    
    // Sauvegarder seulement l'ID du tuteur existant (pas les données complètes)
    handleInputChange('tutor_id', tutor.id);
    // Ne pas sauvegarder les données du tuteur car il existe déjà
  };

  // Gérer la sélection d'un second tuteur existant
  const handleSelectExistingSecondTutor = async (tutor) => {
    if (!tutor) return;
    
    // Vérifier si ce tuteur est déjà sélectionné comme tuteur principal
    if (selectedTutorId === tutor.id) {
      toast.error('Ce tuteur est déjà sélectionné comme maître d\'apprentissage principal');
      return;
    }
    
    // Mettre à jour les données du second tuteur pour l'affichage
    const secondTutorDataObj = {
      last_name: tutor.last_name || '',
      first_name: tutor.first_name || '',
      birth_date: tutor.birth_date || '',
      email: tutor.email || '',
      phone: tutor.phone || '',
      job_title: tutor.job_title || '',
      highest_degree_title: tutor.highest_degree_title || '',
      highest_degree_level: tutor.highest_degree_level || '',
      verified: tutor.verified || false,
      is_apt_tutor: tutor.is_apt_tutor || false,
    };
    
    setSecondTutorData(secondTutorDataObj);
    setSelectedSecondTutorId(tutor.id);
    
    // Sauvegarder seulement l'ID du second tuteur existant (pas les données complètes)
    handleInputChange('second_tutor_id', tutor.id);
    // Ne pas sauvegarder les données du tuteur car il existe déjà
  };


  // Valider si toutes les informations requises sont saisies
  const validateTutorInfo = () => {
    const tutorValid = tutorData.first_name && tutorData.last_name && tutorData.email;
    const secondTutorValid = hasSecondTutor === 'Non' || 
      (secondTutorData.first_name && secondTutorData.last_name && secondTutorData.email);
    
    return tutorValid && secondTutorValid;
  };

  // Exposer la fonction de validation au composant parent
  useEffect(() => {
    if (updateData && typeof updateData === 'function') {
      updateData('isValid', validateTutorInfo());
    }
  }, [tutorData, secondTutorData, hasSecondTutor]);

  return (
    <>
      <Card className={cn(styles.card, className)} title="Maître d'apprentissage" classTitle="title-yellow">
        <div className={styles.modeSelector}>
          <DualButton
            label="Mode de saisie du tuteur"
            option1="Rechercher"
            option2="Créer nouveau"
            value={tutorMode === 'search' ? 'Rechercher' : 'Créer nouveau'}
            onChange={(value) => setTutorMode(value === 'Rechercher' ? 'search' : 'create')}
            multiSelect={false}
          />
        </div>
        
        {tutorMode === 'search' ? (
          <TutorSearchBar
            className={styles.searchBar}
            label="Rechercher un tuteur existant"
            onSelectTutor={handleSelectExistingTutor}
            showDetails={false}
            excludeTutorIds={selectedSecondTutorId ? [selectedSecondTutorId] : []}
          />
        ) : (
        <div className={styles.group}>
          <TextInput
            className={styles.field}
            label="Nom"
            name="last_name"
            value={tutorData.last_name}
            onChange={handleTutorChange}
            required
          />
          <TextInput
            className={styles.field}
            label="Prénom"
            name="first_name"
            value={tutorData.first_name}
            onChange={handleTutorChange}
            required
          />
          <TextInput
            className={styles.field}
            label="Date de naissance"
            name="birth_date"
            type="date"
            value={tutorData.birth_date}
            onChange={handleTutorChange}
          />

          <TextInput
            className={styles.field}
            label="Email"
            name="email"
            type="email"
            value={tutorData.email}
            onChange={handleTutorChange}
            required
          />
          <PhoneInput
            className={styles.field}
            label="Numéro de téléphone"
            name="phone"
            id="phone"
            placeholder="Numéro de téléphone"
            value={tutorData.phone || ''}
            onChange={(phone) => {
              setTutorData(prev => ({
                ...prev,
                phone: phone.target.value
              }));
              handleInputChange('tutor', { ...tutorData, phone: phone.target.value });
            }}
            autoComplete="tel"
            required
          />
          <TextInput
            className={styles.field}
            label="Poste occupé"
            name="job_title"
            value={tutorData.job_title}
            onChange={handleTutorChange}
          />

          <div className={styles.field}>
            <Dropdown
              className={styles.dropdown}
              label="Niveau du diplôme"
              name="highest_degree_level"
              value={DEGREE_LEVEL_OPTIONS.find(option => option.value === tutorData.highest_degree_level) || tutorData.highest_degree_level}
              options={DEGREE_LEVEL_OPTIONS}
              setValue={(value) => {
                const degreeLevel = value.value || value;
                setTutorData(prev => ({
                  ...prev,
                  highest_degree_level: degreeLevel
                }));
                handleInputChange('tutor', { ...tutorData, highest_degree_level: degreeLevel });
              }}
            />
          </div>

          <TextInput
            className={styles.field}
            label="Intitulé du diplôme obtenu"
            name="highest_degree_title"
            value={tutorData.highest_degree_title}
            onChange={handleTutorChange}
          />

        </div>
        )}

        {tutorMode === 'create' && (
          <div className={styles.switchContainer}>
            <span className={styles.switchLabel}>L'employeur atteste sur l'honneur que le maitre d'apprentissage répond à l'ensemble des critères d'éligibilités à cette fonction.</span>
            <Switch
              name="verified"
              value={tutorData.verified}
              onChange={(value) => handleTutorSwitchChange('verified', value)}
            />
          </div>
        )}

        <div className={styles.field}>
          <DualButton
            label="Existe t'il un second maître d'apprentissage ?"
            option1="Oui"
            option2="Non"
            value={hasSecondTutor}
            onChange={(value) => handleDualButtonChange('has_second_tutor', value)}
            multiSelect={false}
          />
        </div>

      </Card>

      {hasSecondTutor === 'Oui' && (
        <Card className={cn(styles.card, className)} title="Second maître d'apprentissage" classTitle="title-yellow">
          <div className={styles.modeSelector}>
            <DualButton
              label="Mode de saisie du second tuteur"
              option1="Rechercher"
              option2="Créer nouveau"
              value={secondTutorMode === 'search' ? 'Rechercher' : 'Créer nouveau'}
              onChange={(value) => setSecondTutorMode(value === 'Rechercher' ? 'search' : 'create')}
              multiSelect={false}
            />
          </div>
          
          {secondTutorMode === 'search' ? (
            <TutorSearchBar
              className={styles.searchBar}
              label="Rechercher un second tuteur existant"
              onSelectTutor={handleSelectExistingSecondTutor}
              showDetails={false}
              excludeTutorIds={selectedTutorId ? [selectedTutorId] : []}
            />
          ) : (
          <div className={styles.group}>
            <TextInput
              className={styles.field}
              label="Nom"
              name="last_name"
              value={secondTutorData.last_name}
              onChange={handleSecondTutorChange}
              required
            />
            <TextInput
              className={styles.field}
              label="Prénom"
              name="first_name"
              value={secondTutorData.first_name}
              onChange={handleSecondTutorChange}
              required
            />
            <TextInput
              className={styles.field}
              label="Date de naissance"
              name="birth_date"
              type="date"
              value={secondTutorData.birth_date}
              onChange={handleSecondTutorChange}
            />

            <TextInput
              className={styles.field}
              label="Email"
              name="email"
              type="email"
              value={secondTutorData.email}
              onChange={handleSecondTutorChange}
              required
            />
            <PhoneInput
              className={styles.field}
              label="Numéro de téléphone"
              name="phone"
              id="second_tutor_phone"
              placeholder="Numéro de téléphone"
              value={secondTutorData.phone || ''}
              onChange={(phone) => {
                setSecondTutorData(prev => ({
                  ...prev,
                  phone: phone.target.value
                }));
                handleInputChange('second_tutor', { ...secondTutorData, phone: phone.target.value });
              }}
              autoComplete="tel"
              required
            />
            <TextInput
              className={styles.field}
              label="Poste occupé"
              name="job_title"
              value={secondTutorData.job_title}
              onChange={handleSecondTutorChange}
            />

            <div className={styles.field}>
              <Dropdown
                className={styles.dropdown}
                label="Niveau du diplôme"
                name="highest_degree_level"
                value={DEGREE_LEVEL_OPTIONS.find(option => option.value === secondTutorData.highest_degree_level) || secondTutorData.highest_degree_level}
                options={DEGREE_LEVEL_OPTIONS}
                setValue={(value) => {
                  const degreeLevel = value.value || value;
                  setSecondTutorData(prev => ({
                    ...prev,
                    highest_degree_level: degreeLevel
                  }));
                  handleInputChange('second_tutor', { ...secondTutorData, highest_degree_level: degreeLevel });
                }}
              />
            </div>

            <TextInput
              className={styles.field}
              label="Intitulé du diplôme obtenu"
              name="highest_degree_title"
              value={secondTutorData.highest_degree_title}
              onChange={handleSecondTutorChange}
            />
          </div>
          )}

          {secondTutorMode === 'create' && (
            <div className={styles.switchContainer}>
              <span className={styles.switchLabel}>Le second maître d'apprentissage est-il apte à accueillir un élève en apprentissage ?</span>
              <Switch
                name="verified"
                value={secondTutorData.verified}
                onChange={(value) => handleSecondTutorSwitchChange('verified', value)}
              />
            </div>
          )}
        </Card>
      )}
    </>
  );
};


export default TutorInfo;
